import { useEffect, Suspense } from 'react';
import includes from 'lodash/includes';
import Box from '@mui/material/Box';
import { useGetMerchant, getQueryData, isUnhandledErrorQuery } from '@repo/api/endpoints/merchant';
import { useMainContext } from '@repo/app-contexts/main';
import { colorPalette } from '@repo/mui-config/color-palette';
import { useIsMobile } from '@repo/mui-config/hooks';
import { usePrevious } from '@repo/hooks';
import { useProviderStore } from '@repo/store/provider';
import { getDefaultProvider, isAvailableMultiProvider } from '@repo/store/helpers';
import { BreakpointIndicator, AppLoading } from '@repo/ui';
import { useSpecialOfferDeadlineAction } from '@repo/app-contexts/promotion';

import { VIEW_TYPE, ZERO_VIEW_TYPE } from '@/store/view.type';
import { useMainHubStoreActions, useMainHubStoreState } from '@/store/main-hub.store';
import { setCurrentViewForMerchantHashResponse } from '@/store/set-current-view-for-merchant-hash-response';

import { renderCurrentView } from './render-current-view';

export function MainHub() {
  const { setAppState, setCurrentView } = useMainHubStoreActions();
  const { currentViewType, merchantResponseData } = useMainHubStoreState();
  const prevViewType = usePrevious(currentViewType);
  const { hash, token, status } = useMainContext();
  const merchantQueryData = useGetMerchant({ hash, token, skipDelay: true });
  const { status: merchantStatus, refetch } = merchantQueryData;
  const isMobile = useIsMobile();
  useSpecialOfferDeadlineAction();

  const {
    state: { currentProvider },
    actions: { setCurrentProvider, setIsAvailableMultiprovider },
  } = useProviderStore();

  const prevCurrentProvider = usePrevious(currentProvider);

  /**
   * Fetch data when user goes to dashboard from the loan form to feed the dashboard with actual loan data
   */
  useEffect(() => {
    if (
      currentViewType === VIEW_TYPE.DASHBOARD &&
      (prevViewType === VIEW_TYPE.LOAN_FORM_VIEW || prevViewType === VIEW_TYPE.SELECT_OFFER_VIEW)
    ) {
      void refetch();
    }
  }, [currentViewType]);
  /**
   * Set invalidated query
   */
  const stringifyMerchantResponseData = JSON.stringify(merchantResponseData);
  const stringifyMerchantQueryResultData = JSON.stringify(merchantQueryData);
  useEffect(() => {
    if (getQueryData(merchantQueryData) !== merchantResponseData) {
      setAppState(currentViewType, merchantQueryData);
    }
  }, [stringifyMerchantResponseData, stringifyMerchantQueryResultData]);

  /**
   * Set an appointed provider
   */
  useEffect(() => {
    const resolvedData = getQueryData(merchantQueryData);
    const merchantData =
      resolvedData !== 'RESPONSE_422_VALIDATION_ERROR' ? resolvedData : undefined;
    const defaultProvider = getDefaultProvider(merchantData);
    setIsAvailableMultiprovider(isAvailableMultiProvider(merchantData));
    if (!currentProvider && defaultProvider) {
      setCurrentProvider(defaultProvider);
    }
  }, [stringifyMerchantQueryResultData]);
  /**
   * Set the current application state
   */
  useEffect(() => {
    if (!prevCurrentProvider && currentProvider) {
      const viewType = setCurrentViewForMerchantHashResponse({
        merchantResponseData: merchantQueryData,
        currentProvider,
      });
      setAppState(viewType, merchantQueryData);
    }
  }, [merchantStatus, currentProvider, prevCurrentProvider]);
  /**
   * useGetIFrame needs hash and token.
   * If either of them is not returned successfully,
   * the status is set to error, and useGetIFrame cannot be invoked.
   */
  useEffect(() => {
    if (status === 'error' || isUnhandledErrorQuery(merchantQueryData)) {
      setCurrentView(VIEW_TYPE.SERVICE_NOT_AVAILABLE);
    }
  }, [status, merchantStatus]);

  let Component =
    merchantStatus === 'pending'
      ? renderCurrentView[VIEW_TYPE.APP_LOADING]
      : renderCurrentView[currentViewType];

  if (isUnhandledErrorQuery(merchantQueryData)) {
    Component = renderCurrentView[VIEW_TYPE.SERVICE_NOT_AVAILABLE];
  }

  const getMainHeight = () => {
    if (currentViewType === VIEW_TYPE.APP_LOADING) {
      return '100%';
    }

    if (currentViewType === VIEW_TYPE.LOAN_APPLICATION_POSSIBLE_WITHOUT_SELECTED_MARKETING_DATA) {
      return 'auto';
    }

    return includes(
      [...Object.keys(ZERO_VIEW_TYPE), VIEW_TYPE.SERVICE_NOT_AVAILABLE, VIEW_TYPE.PLAYGROUND],
      currentViewType,
    )
      ? { xs: 'auto', lg: '100%' }
      : 'auto';
  };

  return (
    <Box
      component="main"
      sx={{
        backgroundColor: colorPalette.cloud.light,
        height: getMainHeight(),
        minHeight: '100%',
        px: isMobile ? '8px' : '20px',
        py: isMobile ? '8px' : '14px',
        display: 'flex',
      }}
    >
      {__IS_TESTMODE_STANDALONE__ && <BreakpointIndicator />}
      <Suspense fallback={<AppLoading />}>
        <Component />
      </Suspense>
    </Box>
  );
}
